import { faCubes, faGripVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { toast } from 'react-toastify';
import RolesContext from '../../contexts/RolesContext';
import { AssetType, assetTypesApi } from '../../utils/api/asset';
import ConfirmationModal from '../Shared/ConfirmationModal/ConfirmationModal';
import AssetTypeEditModal from './AssetTypeEditModal';

interface IAssetTypesAdminProps {
  divisionId: number;
}

const AssetTypesAdmin: React.FC<IAssetTypesAdminProps> = ({ divisionId }) => {
  const permCheck = useContext(RolesContext).userHasPermission;
  const canHandleAssets = permCheck('all', 'Asset Types');

  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [openAssetTypeModal, setOpenAssetTypeModal] = useState(false);
  const [editType, setEditType] = useState<AssetType | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AssetType | null>(null);

  const loadTypes = useCallback(() => {
    if (!divisionId) return;
    assetTypesApi
      .index(divisionId)
      .then(setAssetTypes)
      .catch(() => toast.error('Failed to load asset types.'));
  }, [divisionId]);

  useEffect(() => {
    loadTypes();
  }, [loadTypes]);

  const handleDragEnd = async (result: DropResult) => {
    if (!result.destination || result.destination.index === result.source.index)
      return;

    const reordered = [...assetTypes];
    const [moved] = reordered.splice(result.source.index, 1);

    reordered.splice(result.destination.index, 0, moved);

    const withOrder = reordered.map((t, i) => ({ ...t, display_order: i }));

    setAssetTypes(withOrder);
    try {
      await assetTypesApi.reorder(
        withOrder.map((t) => ({ id: t.id, display_order: t.display_order })),
      );
      toast.success('Order updated');
    } catch {
      toast.error('Failed to save order.');
      loadTypes();
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await assetTypesApi.destroy(deleteTarget.id);
      toast.success('Asset type deleted');
      setOpenAssetTypeModal(false);
      setEditType(null);
      loadTypes();
    } catch {
      toast.error('Failed to delete asset type. Please try again.');
    } finally {
      setDeleteTarget(null);
    }
  };

  return (
    <section className='asset-config'>
      <div className='asset-config__heading'>
        <span className='asset-config__heading-icon'>
          <FontAwesomeIcon icon={faCubes} />
        </span>
        <div className='asset-config__heading-text'>
          <h3>
            Asset Types
            <span className='asset-config__count'>{assetTypes.length}</span>
          </h3>
        </div>
        {canHandleAssets && (
          <button
            className='asset-config__add'
            onClick={() => setOpenAssetTypeModal(true)}
          >
            + New Asset Type
          </button>
        )}
      </div>

      <div className='asset-config__table-wrap'>
        <table className='asset-config__table asset-config__table--types'>
          <thead>
            <tr>
              {canHandleAssets && <th aria-label='drag' />}
              <th>Code</th>
              <th>Name</th>
              <th>Description</th>
              <th>Type</th>
              <th aria-label='actions' />
            </tr>
          </thead>
          <tbody>
            {assetTypes
              .filter((t) => t.is_system)
              .map((type: AssetType) => (
                <tr key={type.id}>
                  <td className='asset-config__drag-handle'></td>
                  <td>
                    <span
                      className='asset-type-badge'
                      style={{
                        backgroundColor:
                          type.featureable_data?.config?.color || '#888',
                      }}
                    >
                      {type.code}
                    </span>
                  </td>
                  <td>
                    <span className='asset-config__name-cell'>{type.name}</span>
                  </td>
                  <td>
                    <span className='asset-config__desc'>
                      {type.description || '—'}
                    </span>
                  </td>
                  <td>
                    <span className='asset-config__badge-soft'>System</span>
                  </td>
                  <td className='asset-config__row-actions'>
                    <button
                      className='tiny outline'
                      onClick={() => {
                        setEditType(type);
                        setOpenAssetTypeModal(true);
                      }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='asset-types'>
              {(provided) => (
                <tbody ref={provided.innerRef} {...provided.droppableProps}>
                  {assetTypes
                    .filter((t) => !t.is_system)
                    .map((type: AssetType) => (
                      <Draggable
                        key={type.id}
                        draggableId={String(type.id)}
                        index={type.display_order}
                        isDragDisabled={!canHandleAssets}
                      >
                        {(drag) => (
                          <tr
                            ref={drag.innerRef}
                            {...drag.draggableProps}
                            style={drag.draggableProps.style}
                          >
                            <td
                              className='asset-config__drag-handle'
                              {...(canHandleAssets ? drag.dragHandleProps : {})}
                            >
                              {canHandleAssets && (
                                <FontAwesomeIcon icon={faGripVertical} />
                              )}
                            </td>
                            <td>
                              <span
                                className='asset-type-badge'
                                style={{
                                  backgroundColor:
                                    type.featureable_data?.config?.color ||
                                    '#888',
                                }}
                              >
                                {type.code}
                              </span>
                            </td>
                            <td>
                              <span className='asset-config__name-cell'>
                                {type.name}
                              </span>
                            </td>
                            <td>
                              <span className='asset-config__desc'>
                                {type.description || '—'}
                              </span>
                            </td>
                            <td>
                              <span className='asset-config__badge-soft is-teal'>
                                Custom
                              </span>
                            </td>
                            <td className='asset-config__row-actions'>
                              <button
                                className='tiny outline'
                                onClick={() => {
                                  setEditType(type);
                                  setOpenAssetTypeModal(true);
                                }}
                              >
                                {canHandleAssets ? 'Edit' : 'View'}
                              </button>
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                  {provided.placeholder}
                  {assetTypes.length === 0 && (
                    <tr>
                      <td colSpan={6}>
                        <div className='asset-config__empty'>
                          <FontAwesomeIcon icon={faCubes} />
                          <strong>No asset types yet</strong>
                          <span>
                            Create your first asset type to start placing assets
                            on project maps.
                          </span>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </Droppable>
          </DragDropContext>
        </table>
      </div>

      {openAssetTypeModal && (
        <AssetTypeEditModal
          divisionId={divisionId}
          assetType={editType}
          length={assetTypes.length}
          onSaved={() => {
            loadTypes();
            setOpenAssetTypeModal(false);
          }}
          onClose={() => {
            setEditType(null);
            setOpenAssetTypeModal(false);
          }}
          onDelete={() => {
            setDeleteTarget(editType);
          }}
        />
      )}

      <ConfirmationModal
        show={!!deleteTarget}
        title={`Delete "${deleteTarget?.name}"?`}
        subTitle='This also affects any project assets using this type.'
        buttonText='Delete Asset Type'
        handleConfirm={handleDelete}
        handleClose={() => setDeleteTarget(null)}
      />
    </section>
  );
};

export default AssetTypesAdmin;
